import { useState, useRef, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer
} from "recharts";

import "./CompressorTrendFaceplate.css";

/* ======================================================
   FACEPLATE COMPONENT
====================================================== */

function CompressorTrendFaceplate({ data = {}, close }) {

  /* ======================================================
     DRAGGING
  ====================================================== */

  const [position, setPosition] = useState({ x: 200, y: 100 });
  const [dragging, setDragging] = useState(false);
  const offset = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setDragging(true);
    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;
      setPosition({
        x: e.clientX - offset.current.x,
        y: e.clientY - offset.current.y
      });
    };

    const up = () => setDragging(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, [dragging]);

  /* ======================================================
     ZOOM + TIME SCROLL
  ====================================================== */

  const [zoom, setZoom] = useState(100);
  const [offsetIndex, setOffsetIndex] = useState(0);

  /* ======================================================
     HISTORIAN SAFETY CHECK
  ====================================================== */

  const history = useMemo(() => {
    return Array.isArray(data?.history) ? data.history : [];
  }, [data]);

  /* ======================================================
     SLICE DATA (ZOOM + SCROLL)
  ====================================================== */

  const chartData = useMemo(() => {

    if (!history.length) return [];

    const size = Math.floor((history.length * zoom) / 100);
    const start = Math.max(0, history.length - size - offsetIndex);

    return history
      .slice(start, start + size)
      .map((p) => ({
        time: new Date(p.t).toLocaleTimeString(),

        PT_suction: p.PT_suction,
        PT_discharge: p.PT_discharge,

        TT_suction: p.TT_suction,
        TT_discharge: p.TT_discharge,

        FIC_suction: p.FIC_suction,
        FIC_discharge: p.FIC_discharge,

        speed: p.speed
      }));

  }, [history, zoom, offsetIndex]);

  /* ======================================================
     VARIABLE SELECTION
  ====================================================== */

  const [selected, setSelected] = useState([
    "PT_suction",
    "PT_discharge",
    "speed"
  ]);

  const toggle = (key) => {
    setSelected((prev) =>
      prev.includes(key)
        ? prev.filter((x) => x !== key)
        : [...prev, key]
    );
  };

  /* ======================================================
     UI
  ====================================================== */

  return (
    <div
      className="trend-faceplate"
      style={{ left: position.x, top: position.y }}
    >

      {/* HEADER */}
      <div className="trend-header" onMouseDown={handleMouseDown}>
        <div className="trend-title">
          COMPRESSOR TREND HISTORIAN
        </div>

        <button className="close-btn" onClick={close}>
          ✕
        </button>
      </div>

      {/* BODY */}
      <div className="trend-body">

        {/* SIDEBAR */}
        <div className="trend-sidebar">

          {/* ZOOM */}
          <div className="trend-section-title">ZOOM</div>
          <input
            type="range"
            min="10"
            max="100"
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />

          {/* TIME SCROLL */}
          <div className="trend-section-title">TIME SCROLL</div>
          <input
            type="range"
            min="0"
            max={Math.max(0, chartData.length - 10)}
            value={offsetIndex}
            onChange={(e) => setOffsetIndex(Number(e.target.value))}
          />

          {/* VARIABLES */}
          <div className="trend-section-title">VARIABLES</div>

          {[
            "PT_suction",
            "PT_discharge",
            "TT_suction",
            "TT_discharge",
            "FIC_suction",
            "FIC_discharge",
            "speed"
          ].map((key) => (
            <label key={key} className="trend-checkbox">
              <input
                type="checkbox"
                checked={selected.includes(key)}
                onChange={() => toggle(key)}
              />
              {key}
            </label>
          ))}

        </div>

        {/* CHART */}
        <div className="trend-chart">

          <ResponsiveContainer width="100%" height="100%">

            <LineChart data={chartData}>

              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />

              {selected.includes("PT_suction") && (
                <Line type="monotone" dataKey="PT_suction" stroke="#00ffae" dot={false} />
              )}

              {selected.includes("PT_discharge") && (
                <Line type="monotone" dataKey="PT_discharge" stroke="#00d2ff" dot={false} />
              )}

              {selected.includes("TT_suction") && (
                <Line type="monotone" dataKey="TT_suction" stroke="#ff66cc" dot={false} />
              )}

              {selected.includes("TT_discharge") && (
                <Line type="monotone" dataKey="TT_discharge" stroke="#ff3300" dot={false} />
              )}

              {selected.includes("speed") && (
                <Line type="monotone" dataKey="speed" stroke="#ffcc00" dot={false} />
              )}

            </LineChart>

          </ResponsiveContainer>

        </div>
      </div>

      {/* FOOTER */}
      <div className="trend-footer">
        <button onClick={() => window.print()}>PRINT</button>
        <button onClick={() => navigator.clipboard.writeText(window.location.href)}>
          SHARE
        </button>
      </div>

    </div>
  );
}

export default CompressorTrendFaceplate;